import React, { useState, useEffect, useRef } from 'react';
import AdminLayout from './adminlayout';
import './adminstyle/FounderManagement.css';

// JWT Fetch Helper
const authFetch = (url, options = {}) => {
  const token = localStorage.getItem('token');
  const headers = {
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...options.headers,
  };
  if (!(options.body instanceof FormData)) {
    headers['Content-Type'] = 'application/json';
  }
  return fetch(url, {
    ...options,
    headers,
  });
};

const FounderManagement = () => {
  const [founder, setFounder] = useState({
    name: '',
    credentials: '',
    biography: '',
    quote: '',
    image_url: '',
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  
  // Local states for the selected image file and preview url
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');
  
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchFounderDetails();
  }, []);

  const fetchFounderDetails = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/founder');
      if (res.ok) {
        const data = await res.json();
        setFounder(data);
        setPreviewUrl(data.image_url);
      } else {
        setError('Failed to fetch founder details.');
      }
    } catch (err) {
      console.error(err);
      setError('Could not connect to backend server.');
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFounder(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        alert('Image must be less than 10MB.');
        return;
      }
      setSelectedFile(file);
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSuccess('');
    setError('');

    try {
      const formData = new FormData();
      formData.append('name', founder.name);
      formData.append('credentials', founder.credentials);
      formData.append('biography', founder.biography);
      formData.append('quote', founder.quote);

      if (selectedFile) {
        formData.append('image', selectedFile);
      }

      const res = await authFetch('/api/founder', {
        method: 'PUT',
        body: formData,
      });

      if (res.ok) {
        const updated = await res.json();
        setFounder(updated);
        setPreviewUrl(updated.image_url);
        setSelectedFile(null);
        setSuccess('Founder details saved successfully!');
        window.scrollTo({ top: 0, behavior: 'smooth' });
      } else {
        const errData = await res.json();
        setError(errData.message || 'Failed to update founder details.');
      }
    } catch (err) {
      console.error(err);
      setError('Connection failed. Could not save changes.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Revert form state to the current founder data
    setPreviewUrl(founder.image_url);
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setError('');
    setSuccess('');
  };

  return (
    <AdminLayout>
      <div className="founder-mgmt-wrapper">
        <div className="admin-page-header">
          <h1>FOUNDER SETTINGS</h1>
        </div>

        {loading ? (
          <div className="fm-loading-container">
            <div className="fm-spinner"></div>
            <p>Loading founder information...</p>
          </div>
        ) : (
          <div className="fm-content-container">
            {success && <div className="fm-alert fm-alert-success">{success}</div>}
            {error && <div className="fm-alert fm-alert-error">{error}</div>}

            <form onSubmit={handleSave} className="fm-glass-card">
              <div className="fm-split-layout">
                
                {/* ── Left Side: Photo Management ── */}
                <div className="fm-photo-column">
                  <h3 className="fm-section-title">Founder Portrait</h3>
                  
                  <div className="fm-photo-display-box">
                    <img 
                      src={previewUrl || '/images/Ceo/Dr Melvin mathew.png'} 
                      alt="Founder Preview" 
                      className="fm-portrait-preview"
                    />
                    
                    <div className="fm-photo-overlay" onClick={() => fileInputRef.current?.click()}>
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                        <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/>
                        <circle cx="12" cy="13" r="4"/>
                      </svg>
                      <span>Update Photo</span>
                    </div>
                  </div>

                  <input 
                    type="file" 
                    ref={fileInputRef}
                    accept="image/*"
                    onChange={handleImageChange}
                    style={{ display: 'none' }}
                  />

                  <button 
                    type="button" 
                    className="fm-file-select-btn"
                    onClick={() => fileInputRef.current?.click()}
                  >
                    Choose New Image
                  </button>
                  <p className="fm-photo-hint">Recommended aspect ratio: 8:7 (e.g. 480x420px). Supports PNG, JPG, JPEG up to 10MB.</p>
                </div>

                {/* ── Right Side: Text Information ── */}
                <div className="fm-fields-column">
                  <h3 className="fm-section-title">Biography & Message</h3>

                  <div className="fm-form-group">
                    <label className="fm-label">Full Name</label>
                    <input 
                      type="text" 
                      name="name"
                      className="fm-input" 
                      placeholder="e.g. Dr. Melvin Mathew"
                      value={founder.name}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="fm-form-group">
                    <label className="fm-label">Specialization</label>
                    <input 
                      type="text" 
                      name="credentials"
                      className="fm-input" 
                      placeholder="e.g. Founder & Chief Dental Surgeon | BDS, FAGE"
                      value={founder.credentials}
                      onChange={handleInputChange}
                      required
                    />
                  </div>

                  <div className="fm-form-group">
                    <label className="fm-label">Founder Biography / Message</label>
                    <textarea 
                      name="biography"
                      className="fm-textarea" 
                      rows={6}
                      maxLength={390}
                      placeholder="Write a warm, welcoming message detailing the founder's vision and credentials..."
                      value={founder.biography}
                      onChange={handleInputChange}
                      required
                    />
                    <div style={{ fontSize: '11px', color: '#64748b', textAlign: 'right', marginTop: '4px' }}>
                      {390 - (founder.biography?.length || 0)} / 390 characters remaining
                    </div>
                  </div>

                  <div className="fm-form-group">
                    <label className="fm-label">Inspirational Quote</label>
                    <textarea 
                      name="quote"
                      className="fm-textarea" 
                      rows={3}
                      maxLength={140}
                      placeholder="e.g. Our mission is — A smile is an inexpensive way to change your look."
                      value={founder.quote}
                      onChange={handleInputChange}
                      required
                    />
                    <div style={{ fontSize: '11px', color: '#64748b', textAlign: 'right', marginTop: '4px' }}>
                      {140 - (founder.quote?.length || 0)} / 140 characters remaining
                    </div>
                  </div>

                </div>

              </div>

              {/* ── Action Buttons ── */}
              <div className="fm-actions-bar">
                <button 
                  type="button" 
                  className="fm-btn fm-btn-secondary" 
                  onClick={handleCancel}
                  disabled={saving}
                >
                  Cancel Changes
                </button>
                <button 
                  type="submit" 
                  className="fm-btn fm-btn-primary"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <span className="fm-button-spinner"></span>
                      Saving...
                    </>
                  ) : 'Save Settings'}
                </button>
              </div>

            </form>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default FounderManagement;
